import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Input, Button } from '../../index'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'

import SelectField from './SelectField'
import TextField from './TextField'
import PhoneField from './PhoneField'
import HoneypotField from './HoneypotField'

const HONEYPOT_MIN_TIME_MS = 3000
// A name no autofill or form-filling tool takes for a real field, so a person never fills it.
const honeypotFieldName = 'dt_contact_extra'

const validationSchema = Yup.object({
  'sender-name': Yup.string().required("Please enter your name."),
  'sender-phone': Yup.string().length(12, "Please enter a valid phone number.").required("Please enter your phone number."),
  'sender-email': Yup.string().email("Please enter a valid email.").required("Please enter your email address."),
  reason: Yup.string(),
  discovery: Yup.string(),
  comment: Yup.string(),
  [honeypotFieldName]: Yup.string().max(0),
})

const initialValues = {
  'sender-name': '',
  'sender-phone': '',
  'sender-email': '',
  reason: '',
  discovery: '',
  comment: '',
  [honeypotFieldName]: '',
}

const endpoint = process.env.NEXT_PUBLIC_CF7_ENDPOINT
const formId = process.env.NEXT_PUBLIC_CF7_FORM_ID
// Optional: the reCAPTCHA v3 site key the CMS checks against (Contact > Integration). When set, a
// token goes with every message.
const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
// Optional: "on" loads CleanTalk's bot detector, whose token lets CleanTalk on the CMS tell a person
// from a script.
const cleanTalkOn = process.env.NEXT_PUBLIC_CLEANTALK_BOT_DETECTOR === 'on'
const tooFastMessage = 'Please take a moment to check your details, then send again.'

const loadScript = (src, marker) => {
  if (document.querySelector(`script[data-${marker}]`)) return
  const script = document.createElement('script')
  script.src = src
  script.async = true
  script.dataset[marker] = '1'
  document.head.appendChild(script)
}

// A reCAPTCHA v3 token for this message, or '' without a key, without the script, on an error or after
// 10 seconds. execute() can throw instead of rejecting (a key the script did not load, say), so both
// calls are guarded and the message goes at once.
const getRecaptchaToken = () => new Promise(resolve => {
  if (!recaptchaSiteKey || !window.grecaptcha) return resolve('')
  const timer = setTimeout(() => resolve(''), 10000)
  const finish = token => { clearTimeout(timer); resolve(token || '') }
  try {
    window.grecaptcha.ready(() => {
      try {
        window.grecaptcha.execute(recaptchaSiteKey, { action: 'contact' }).then(finish, () => finish(''))
      } catch (error) {
        finish('')
      }
    })
  } catch (error) {
    finish('')
  }
})

// CleanTalk's bot-detector token, which its script keeps in local storage; '' when there is none.
const getCleanTalkToken = () => {
  try {
    const token = JSON.parse(window.localStorage.getItem('bot_detector_event_token') || 'null')?.value
    return typeof token === 'string' && token.length === 64 ? token : ''
  } catch (error) {
    return ''
  }
}

const makeSelectOptions = (arr) => arr.map(option => ({ value: option, label: option }))

const discoveryOptions = makeSelectOptions([
  "Architect/Designer",
  "Previous Client",
  "Realtor",
  "Word of Mouth",
  "Social Media/Google",
  "Other",
])

const reasonOptions = makeSelectOptions([
  "Remodel/Addition",
  "New Construction",
  "Employment",
  "Other Inquiry",
])

const messageClassName = 'absolute mt-20 text-xs-B -tracking-3 md:mt-20 md:text-2xl-C'
const internalServerErrorMessage = 'There was an error. Please try again later.'

const ContactForm = ({ className = '' }) => {
  const [successMessage, setSuccessMessage] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loadTime] = useState(Date.now())

  useEffect(() => {
    if (recaptchaSiteKey) {
      loadScript(`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`, 'recaptcha')
    }
    if (cleanTalkOn) {
      loadScript('https://fd.cleantalk.org/ct-bot-detector-wrapper.js', 'cleantalk')
    }
  }, [])

  const handleSubmit = async (values, { resetForm }) => {
    const elapsedMs = Date.now() - loadTime
    if (elapsedMs < HONEYPOT_MIN_TIME_MS) {
      setErrorMessage(tooFastMessage)
      return
    }
    setErrorMessage(null)

    const formData = new FormData()
    Object.keys(values).forEach(key => {
      formData.append(key, values[key]);
    })
    formData.append('_wpcf7_unit_tag', `wpcf7-f${formId}-o1`)
    // How long the form was open, for the CMS's form timer.
    formData.append('_form_elapsed_ms', String(Math.round(elapsedMs)))
    const recaptchaToken = await getRecaptchaToken()
    if (recaptchaToken) formData.append('_wpcf7_recaptcha_response', recaptchaToken)
    const cleanTalkToken = cleanTalkOn ? getCleanTalkToken() : ''
    if (cleanTalkToken) formData.append('ct_bot_detector_event_token', cleanTalkToken)

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
    }

    try {
      const response = await fetch(endpoint, requestOptions)
      const result = await response.json()

      if (result.status == 'mail_sent') {
        resetForm()
        setSuccessMessage(result.message)
      } else {
        setErrorMessage(result.message)
      }
    } catch (error) {
      setErrorMessage(internalServerErrorMessage)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form
          className={`${className} w-full grid md:gap-x-50 gap-y-20 md:gap-y-40`}
        >
          <TextField className="md:col-span-2 w-full" name="sender-name" label="Name *" />
          <PhoneField className="md:col-span-1 w-full" name="sender-phone" label="Phone Number *" />
          <TextField className="md:col-span-1 w-full" name="sender-email" label="Email Address *" />
          <SelectField
            className="md:col-span-1 w-full"
            name="reason"
            label="What are you inquiring about?"
            placeholder=""
            isSearchable={false}
            options={reasonOptions}
          />
          <SelectField
            className="md:col-span-1 w-full"
            name="discovery"
            label="How did you find out about us?"
            placeholder=""
            isSearchable={false}
            options={discoveryOptions}
          />
          <TextField className="md:col-span-2" textarea name="comment" label="Have a comment?" />

          <HoneypotField name={honeypotFieldName} />
          <input type="hidden" name="_load_time" value={loadTime} />

          <div className='mt-15 md:mt-0'>
            <Button
              type="submit"
              text='Send Message'
              icon={isSubmitting ? 'loader' : 'arrow'}
              disabled={isSubmitting}
            />

            {errorMessage && (
              <p className={clsx(messageClassName, 'text-red-500')}>
                {errorMessage}
              </p>
            )}

            {successMessage && (
              <p className={messageClassName}>
                {successMessage}
              </p>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default ContactForm
